import React, { useState, useEffect } from "react";
import "./NavLayout.css";
import { Link, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ChevronDown, Menu, X } from "lucide-react";
import { jwtDecode } from "jwt-decode";

import logo from "./../../images/logo-crypto 2.png";

// Submenu contents carried over verbatim from the pre-revamp Tippy navbar —
// same destinations, new presentation.
const NAV_LINKS = [
  {
    label: "Media",
    to: "/user/blogs",
    children: [
      { label: "Blog", to: "/#blogs", hash: true },
      { label: "Podcast", to: "/#podcast", hash: true },
      { label: "Newsletter", to: "/#newsletter", hash: true },
    ],
  },
  {
    label: "Projects",
    to: "/#projects",
    hash: true,
    children: [
      { label: "View", to: "/#projects", hash: true },
      { label: "Apply", to: "/user/join/crypto?role=founder" },
    ],
  },
  {
    label: "Community",
    to: "https://linktr.ee/thecryptohubcom",
    external: true,
    children: [
      { label: "Linktree", to: "https://linktr.ee/thecryptohubcom", external: true },
      { label: "X / Twitter", to: "https://x.com/thecryptohubcom?mx=2", external: true },
      { label: "Instagram", to: "https://www.instagram.com/thecryptohub_com/", external: true },
      { label: "WhatsApp", to: "https://api.whatsapp.com/send?phone=29", external: true },
      { label: "Telegram", to: "https://t.me/thecryptohub_com", external: true },
    ],
  },
  {
    label: "Events",
    to: "/user/events",
    children: [
      { label: "Calendar", to: "/user/events" },
      { label: "Upcoming Events", to: "/user/events" },
      { label: "Past Events", to: "/user/past/events" },
    ],
  },
];

// Fully opaque with no backdrop blur: the bar must not pick up whatever
// scrolls beneath it — translucency reads as the bar changing colour as the
// page moves.
const NAV_SURFACE = "bg-[#010810] !border-0";

// `minimal` — auth pages (login / signup / forgot password) get a bare bar with
// just the logo, so nothing competes with the form.
const Header = ({ minimal = false }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("loggedin"));
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  // Which mobile section is expanded. One at a time — with all four open the
  // list runs past the viewport before you reach the CTAs.
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const checkTokenExpiry = () => {
      const stored = localStorage.getItem("token");
      if (!stored) return;
      try {
        const decodedToken = jwtDecode(stored);
        const currentTime = Math.floor(Date.now() / 1000);
        if (decodedToken.exp < currentTime) {
          localStorage.removeItem("token");
          localStorage.removeItem("loggedin");
          setToken(null);
          navigate("/user/login");
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    };

    const tokenCheckInterval = setInterval(checkTokenExpiry, 60000);
    return () => clearInterval(tokenCheckInterval);
  }, [navigate]);

  const logoutClicked = () => {
    localStorage.removeItem("loggedin");
    localStorage.removeItem("token");
    setToken(null);
    setOpen(false);
    navigate("/user/login");
  };

  const closeMenu = () => {
    setOpen(false);
    setExpanded(null);
  };

  const renderLink = (link, className) => {
    const shared = {
      key: link.label,
      className,
      onClick: closeMenu,
    };

    if (link.external) {
      return (
        <a
          {...shared}
          href={link.to}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link.label}
        </a>
      );
    }

    if (link.hash) {
      return (
        <HashLink {...shared} to={link.to}>
          {link.label}
        </HashLink>
      );
    }

    return (
      <Link {...shared} to={link.to}>
        {link.label}
      </Link>
    );
  };

  if (minimal) {
    return (
      <nav className={`sticky top-0 w-full z-[1030] ${NAV_SURFACE}`}>
        <div className="flex !justify-center items-center px-4 md:px-10 py-[9px] md:py-[12px] md:min-h-[94px] max-w-6xl mx-auto">
          <Link to="/" className="flex items-center flex-shrink-0">
            <img
            src={logo}
            alt="The Crypto Hub"
            className="h-[43px] sm:h-[50px] md:h-[70px] w-auto"
          />
          </Link>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`sticky top-0 w-full z-[1030] ${NAV_SURFACE}`}>
      {/* Desktop geometry lives in NavLayout.css, ported 1:1 from the
          pre-revamp stylesheet. It must stay `display: flex` with percentage
          widths: the 992-1200 band sums to 110% and relies on flex-shrink. */}
      <div className="navRow relative flex !justify-between items-center gap-4 px-4 py-[9px] w-full">
        {/* Desktop nav */}
        {/* `.firstNav` — width and justification come from NavLayout.css. */}
        <div className="navLeft hidden min-[992px]:flex items-center !flex-nowrap font-headline font-semibold tracking-tight">
          {NAV_LINKS.map((link) => (
            <div
              key={link.label}
              className="relative"
              onMouseEnter={() => setHovered(link.label)}
              onMouseLeave={() => setHovered(null)}
            >
              <span className="flex items-center !flex-nowrap cursor-pointer">
                {renderLink(
                  link,
                  // `!text-*`: Bootstrap's global `a { color: ... }` is loaded
                  // after Tailwind and repaints links on hover otherwise.
                  // `navTopLink` carries the old `.nav-link` size ramp and
                  // `.firstNav a` padding from NavLayout.css. It marks the
                  // top-level links only, so the dropdown children below keep
                  // their own 14px.
                  `navTopLink no-underline transition-colors duration-200 ${
                    hovered === link.label
                      ? "!text-accent"
                      : "!text-white"
                  }`
                )}
              </span>

              {/* Hover bridge — keeps the menu open while crossing the gap */}
              <div
                className={`absolute z-20 left-1/2 -translate-x-1/2 top-full pt-3 min-w-[210px] transition-all duration-200 ${
                  hovered === link.label
                    ? "opacity-100 visible translate-y-0"
                    : "opacity-0 invisible -translate-y-1 pointer-events-none"
                }`}
              >
                {/* `!border-*`: Tailwind's preflight paints a light grey
                    default border colour that wins here otherwise. */}
                <div className="flex flex-col !flex-nowrap gap-0.5 rounded-xl !border !border-[#26262b] bg-[#131315] p-2 shadow-[0_24px_48px_-12px_rgba(0,0,0,0.8)]">
                  {link.children.map((child) =>
                    renderLink(
                      child,
                      // `[display:block]` not `block`: HeaderComponent.css
                      // styles `.mainHeaderComponent .block` as a white card
                      // and wins on specificity.
                      "[display:block] whitespace-nowrap rounded-lg px-4 py-2.5 text-[14px] font-medium !text-[#e5e1e4]/75 no-underline hover:bg-accent/10 hover:!text-accent transition-colors duration-150"
                    )
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Brand — centred in the 40% middle column on desktop, in-flow and
            left-aligned on mobile. */}
        {/* `.topLogoNav` + `.smallblacklogo` — a 188x95 box with the artwork
            contained inside it, from >=992px up. Mobile keeps the Tailwind
            heights. */}
        <Link
          to="/"
          className="navBrand flex items-center flex-shrink-0 justify-center"
        >
          <img
            src={logo}
            alt="The Crypto Hub"
            className="h-[43px] sm:h-[50px] w-auto"
          />
        </Link>

        {/* Desktop CTAs — `.topLogSign`. Width, `justify-center`, the 63px right
            inset and the 8px gap all come from NavLayout.css, as do the three
            fixed button sizes (120x40 / 100x35 / 80x35). */}
        <div className="navCta hidden min-[992px]:flex items-center !flex-nowrap font-headline font-semibold tracking-tight">
          <Link
            to="/user/join/crypto?role=founder"
            // Amber, with near-black label: white on #F0B90A is 1.80:1, which
            // is unreadable, while #1B2129 — the event strip's charcoal —
            // clears 8:1. `!text-` because Bootstrap's global `a { color }`
            // loads after Tailwind and repaints links otherwise.
            className="navCtaPrimary group shrink-0 bg-[#F0B90A] !border !border-[#F0B90A] rounded-[5px] font-bold !text-[#1B2129] no-underline hover:bg-[#D1A008] hover:!border-[#D1A008] transition-all duration-300 active:scale-95 flex items-center !justify-center !flex-nowrap gap-1.5"
          >
            <span className="inline-block group-hover:translate-x-1 transition-transform duration-300">
              Apply
            </span>
            <span className="material-symbols-outlined text-[16px] group-hover:animate-arrow-slide">
              arrow_forward
            </span>
          </Link>

          {token ? (
            <button
              onClick={logoutClicked}
              className="navCtaPrimary shrink-0 flex items-center !justify-center bg-transparent border-0 p-0 !text-white hover:!text-accent cursor-pointer transition-colors duration-200"
            >
              Logout
            </button>
          ) : (
            <>
              <Link
                to="/user/login"
                className="navCtaLogin shrink-0 flex items-center !justify-center !text-white no-underline hover:!text-accent transition-colors duration-200"
              >
                Login
              </Link>
              <Link
                to="/user/join/crypto"
                className="navCtaRegister shrink-0 flex items-center !justify-center !text-white no-underline hover:!text-accent transition-colors duration-200"
              >
                Register
              </Link>
            </>
          )}
        </div>

        {/* Mobile toggle */}
        <button
          className="min-[992px]:hidden p-2 bg-transparent border-0 text-white/80 hover:text-white transition-colors"
          onClick={() => (open ? closeMenu() : setOpen(true))}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu — fills the whole viewport below the sticky bar (61px
          tall, 68px from sm up). `dvh` not `vh` so it tracks Safari's
          collapsing toolbar instead of running underneath it. A list taller
          than the screen scrolls inside the sheet. */}
      {open && (
        <div className="min-[992px]:hidden bg-[#131315] !border-t !border-[#26262b] px-4 py-6 h-[calc(100dvh-61px)] sm:h-[calc(100dvh-68px)] overflow-y-auto overscroll-contain flex flex-col !flex-nowrap">
          <div className="flex flex-col !flex-nowrap">
            {NAV_LINKS.map((link) => (
              <div key={link.label} className="py-2">
                {/* The label still navigates; only the chevron expands, so a
                    tap on "Media" isn't swallowed by the accordion. */}
                <div className="flex !flex-nowrap !justify-between items-center gap-2">
                  {renderLink(
                    link,
                    "[display:block] text-[#e5e1e4] hover:text-accent no-underline font-headline font-semibold py-1.5 transition-colors"
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      setExpanded(expanded === link.label ? null : link.label)
                    }
                    aria-expanded={expanded === link.label}
                    aria-label={`${
                      expanded === link.label ? "Collapse" : "Expand"
                    } ${link.label}`}
                    className="shrink-0 p-2 -mr-2 bg-transparent border-0 text-[#e5e1e4]/60 hover:text-accent cursor-pointer transition-colors"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${
                        expanded === link.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>

                {expanded === link.label && (
                  <div className="flex flex-col !flex-nowrap pl-4 !border-l !border-[#26262b] ml-0.5 mt-1">
                    {link.children.map((child) =>
                      renderLink(
                        child,
                        "[display:block] text-[14px] text-[#e5e1e4]/60 hover:text-accent no-underline font-body py-2 transition-colors"
                      )
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* `mt-auto` parks the CTAs at the bottom of the full-height sheet
              rather than leaving them floating under the last link. */}
          <div className="mt-auto pt-6 flex flex-col !flex-nowrap gap-3">
            <Link
              to="/user/join/crypto?role=founder"
              onClick={closeMenu}
              className="w-full text-center bg-accent !border !border-accent px-8 py-3 rounded-md font-headline font-bold !text-accent-ink no-underline hover:bg-accent-hover hover:!border-accent-hover transition-all duration-300 active:scale-95"
            >
              Apply
            </Link>
            {token ? (
              <button
                onClick={logoutClicked}
                className="w-full text-center border border-outline-variant/30 px-8 py-3 rounded-md font-headline font-bold text-on-surface bg-transparent hover:bg-surface-container-high transition-all"
              >
                Logout
              </button>
            ) : (
              <>
                <Link
                  to="/user/login"
                  onClick={closeMenu}
                  className="w-full text-center border border-outline-variant/30 px-8 py-3 rounded-md font-headline font-bold text-on-surface no-underline hover:bg-surface-container-high transition-all"
                >
                  Login
                </Link>
                <Link
                  to="/user/join/crypto"
                  onClick={closeMenu}
                  className="w-full text-center border border-outline-variant/30 px-8 py-3 rounded-md font-headline font-bold text-on-surface no-underline hover:bg-surface-container-high transition-all"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Header;
